/**
 * Citizen API - Endpoints for citizen submissions
 */

import { apiClient } from './client';
import {
  AuthorityPublicKeyInfo,
  AuthorityPublicInfo,
  AuthorityExtendedInfo,
  FormDefinition,
  FormField,
  EncryptedSubmissionDto,
  SubmissionResponse,
  CitizenProcessDefinition,
  CitizenStatusDto,
  PublicCommentDto,
  BackendFormDefinition,
} from './types';

/**
 * Get authority public key info
 */
export async function getAuthorityPublicKey(
  authorityKeyId: string
): Promise<AuthorityPublicKeyInfo> {
  // Use the authority-keys endpoint for public key
  const response = await apiClient.get<AuthorityPublicInfo>(`/authority-keys/${authorityKeyId}/public-key`);
  // Map to AuthorityPublicKeyInfo format
  return {
    keyId: response.authorityId,
    ownerId: response.authorityId,
    ownerType: 'AUTHORITY',
    ownerName: response.authorityName,
    algorithm: 'RSA-OAEP',
    publicKeyJwk: response.publicKeyJwk,
    fingerprint: response.fingerprint,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Get authority public key info for encryption (V2)
 */
export async function getAuthorityPublicInfo(
  authorityKeyId: string
): Promise<AuthorityPublicInfo> {
  return apiClient.get<AuthorityPublicInfo>(`/authority-keys/${authorityKeyId}/public-key`);
}

/**
 * Get extended authority public info for landing pages
 * Includes profile data like contact info, address, etc.
 */
export async function getAuthorityExtendedInfo(
  authorityKeyId: string
): Promise<AuthorityExtendedInfo> {
  return apiClient.get<AuthorityExtendedInfo>(`/authorities/${authorityKeyId}/public`);
}

/**
 * Get available process definitions for an authority
 */
export async function getProcessesForAuthority(
  authorityKeyId: string
): Promise<CitizenProcessDefinition[]> {
  return apiClient.get<CitizenProcessDefinition[]>(`/citizen/${authorityKeyId}/processes`);
}

// BackendFormDefinition is imported from types.ts

/**
 * Map backend field type to frontend type
 */
function mapFieldType(backendType: string): FormField['type'] {
  const typeMap: Record<string, FormField['type']> = {
    TEXT: 'text',
    TEXTAREA: 'textarea',
    EMAIL: 'email',
    SELECT: 'select',
    CHECKBOX: 'checkbox',
    DATE: 'date',
    NUMBER: 'number',
    PHONE: 'text', // Phone as text with validation
    FILE: 'file',
  };
  return typeMap[backendType] || 'text';
}

/**
 * Transform backend form definition to frontend format
 */
function transformFormDefinition(backend: BackendFormDefinition): FormDefinition {
  // Flatten sections into a single field list for the frontend
  const fields: FormField[] = [];

  for (const section of backend.sections) {
    for (const field of section.fields) {
      // Skip read-only fields for submission forms
      if (field.readOnly) continue;

      const frontendField: FormField = {
        name: field.processVariable,
        type: mapFieldType(field.type),
        label: field.labels['de'] || field.id,
        required: field.required,
        placeholder: field.placeholders['de'],
      };

      // Map options for select fields
      if (field.options && field.options.length > 0) {
        frontendField.options = field.options.map(opt => ({
          value: opt.value,
          label: opt.labels['de'] || opt.value,
        }));
      }

      // Map validation rules
      if (field.validationRules && field.validationRules.length > 0) {
        frontendField.validation = {};
        for (const rule of field.validationRules) {
          if (rule.type === 'MIN_LENGTH' && rule.value) {
            frontendField.validation.min = parseInt(rule.value, 10);
          }
          if (rule.type === 'MAX_LENGTH' && rule.value) {
            frontendField.validation.max = parseInt(rule.value, 10);
          }
          if (rule.type === 'PATTERN' && rule.value) {
            frontendField.validation.pattern = rule.value;
          }
          if (rule.messages['de']) {
            frontendField.validation.message = rule.messages['de'];
          }
        }
      }

      fields.push(frontendField);
    }
  }

  return {
    title: 'Antrag einreichen',
    description: 'Bitte füllen Sie das Formular vollständig aus.',
    fields,
  };
}

/**
 * Get form definition for a process
 * Fetches the form schema from the backend based on the BPMN process definition
 *
 * @throws Error if the form cannot be fetched from the backend
 */
export async function getFormDefinition(
  processKey: string
): Promise<FormDefinition> {
  try {
    const backendForm = await apiClient.get<BackendFormDefinition>(`/citizen/form/${processKey}`);
    return transformFormDefinition(backendForm);
  } catch (error) {
    console.error('Failed to fetch form from backend for process:', processKey, error);
    // Re-throw the error so the UI can handle it appropriately
    throw new Error(`Formular für Prozess "${processKey}" konnte nicht geladen werden.`);
  }
}

/**
 * Submit encrypted application
 */
export async function submitEncryptedApplication(
  authorityKeyId: string,
  submission: EncryptedSubmissionDto
): Promise<SubmissionResponse> {
  return apiClient.post<SubmissionResponse>(
    `/citizen/submit/${authorityKeyId}`,
    submission
  );
}

/**
 * Get submission status by confirmation number
 */
export async function getSubmissionStatus(
  confirmationNumber: string
): Promise<CitizenStatusDto> {
  return apiClient.get<CitizenStatusDto>(`/citizen/status/${confirmationNumber}`);
}

/**
 * Send a citizen message for a submission
 */
export async function sendCitizenMessage(
  confirmationNumber: string,
  message: string
): Promise<PublicCommentDto> {
  return apiClient.post<PublicCommentDto>(
    `/citizen/status/${confirmationNumber}/message`,
    { message }
  );
}

/**
 * Complete a citizen task
 */
export async function completeCitizenTask(
  confirmationNumber: string,
  taskId: string,
  variables: Record<string, unknown>
): Promise<void> {
  await apiClient.post(
    `/citizen/status/${confirmationNumber}/tasks/${taskId}/complete`,
    { variables }
  );
}

/**
 * Upload a document as a citizen
 */
export async function uploadCitizenDocument(
  confirmationNumber: string,
  file: File,
  taskId?: string
): Promise<{ id: string; fileName: string }> {
  const formData = new FormData();
  formData.append('file', file);
  if (taskId) {
    formData.append('taskId', taskId);
  }

  const response = await fetch(
    `/api/v1/citizen/status/${encodeURIComponent(confirmationNumber)}/documents`,
    {
      method: 'POST',
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error(`Document upload failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Transform a backend form definition to the frontend FormDefinition format
 */
export function transformBackendForm(backend: BackendFormDefinition): FormDefinition {
  return transformFormDefinition(backend);
}
